import Image from "next/image";
import React from "react";

interface OldDashboardProps {
  pageUrl: string;
  data: any;
}

type DifficultyObj = { [key: string]: number };

const OldDashboard: React.FC<OldDashboardProps> = ({ pageUrl, data }) => {
  const userAnalytics = data?.data;

  function getDifficultyValue(level: string): number {
    let difficultyArray = userAnalytics?.difficulty as DifficultyObj[];
    if (!difficultyArray || !level) return 0;

    const obj = difficultyArray?.find(
      (diffObj) => Object.keys(diffObj)[0].toLowerCase() === level.toLowerCase()
    );

    return obj ? obj[Object.keys(obj)[0]] : 0;
  }

  const avgScore = (() => {
    const totalScore = userAnalytics?.totalScore;
    const totalPitches = userAnalytics?.totalPitches;

    if (
      typeof totalScore === "number" &&
      typeof totalPitches === "number" &&
      totalPitches > 0
    ) {
      return Number((totalScore / totalPitches).toFixed(2));
    }

    return 0;
  })();

  const capitalizeFirstWord = (text: string) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  return (
    <>
      <div className="flex justify-between">
        <h3 className="heading-sm text-[20px] leading-[40px]">
          Performance Overview
        </h3>
        {/* <Image
                src="/download.svg"
                height={10}
                width={30}
                alt="Download-icon"
              /> */}
      </div>
      <div className="flex gap-4 flex-wrap max-md:min-w-[230px] min-h-[250px] max-md:max-w-[400px] max-md:mx-auto">
        <div className=" flex flex-1 bg-white rounded-2xl p-3 shadow-md flex-col gap-3 justify-between max-md:min-h-[300px]">
          <p className="text-[20px] font-semibold leading-[32px] text-center">
            Overall Performance
          </p>
          <hr className="border-t border-blue-100 " />
          <h2 className="m-auto text-[70px] font-[800] leading-[72px] text-blue-100">
            {avgScore}
          </h2>
          <div className="w-full relative h-[30%]">
            <Image
              src="/chart.png"
              fill
              className="object-contain"
              alt="Chart"
            />{" "}
          </div>
        </div>
        <div className=" flex flex-1 bg-white rounded-2xl p-3 shadow-md flex-col gap-3">
          <p className="text-[20px] font-semibold leading-[32px] text-center">
            Top 5 Strengths
          </p>
          <hr className="border-t border-blue-100 " />

          {userAnalytics?.strengths?.length
            ? userAnalytics?.strengths
                ?.filter((strengthObj: any) => {
                  const key = Object.keys(strengthObj)[0];
                  return key.toLowerCase() !== "n/a"; // ✅ Skip "N/A" or "n/a"
                })
                .map((strengthObj: any) => {
                  const key = Object.keys(strengthObj)[0];
                  return (
                    <p
                      className="text-normal text-black-100 max-md:text-center"
                      key={key}
                    >
                      {capitalizeFirstWord(key)}
                    </p>
                  );
                })
            : "No data available"}
        </div>
        <div className=" flex flex-1 bg-white rounded-2xl p-3 shadow-md flex-col gap-3">
          <p className="text-[20px] font-semibold leading-[32px] text-center">
            Top 5 Weaknesses
          </p>
          <hr className="border-t border-blue-100 " />

          {userAnalytics?.weaknesses?.length
            ? userAnalytics?.weaknesses
                ?.filter((weaknessObj: any) => {
                  const key = Object.keys(weaknessObj)[0];
                  return key.trim().toLowerCase() !== "n/a"; // ✅ Skip "N/A" or "n/a"
                })
                .map((weaknessObj: any) => {
                  const key = Object.keys(weaknessObj)[0];
                  return (
                    <p
                      className="text-normal text-black-100 max-md:text-center"
                      key={key}
                    >
                      {capitalizeFirstWord(key)}
                    </p>
                  );
                })
            : "No data available"}
        </div>
      </div>
      <h3 className="heading-sm text-[20px] leading-[40px]">Overview</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div className="border-1 border-blue-100 rounded-2xl bg-white py-5 px-4 flex flex-col space-x-2">
          <span className="sm:text-[40px] text-2xl font-bold text-black-200">
            {userAnalytics.totalPitches ?? 0}
          </span>
          <span className="text-black-200 text-[16px] font-medium">
            pitches delivered
          </span>
        </div>

        <div className="border-1 border-blue-100 rounded-2xl bg-white py-5 px-4 flex flex-col space-x-2">
          <span className="sm:text-[40px] text-2xl font-bold text-black-200">
            {userAnalytics.industries.length}
          </span>
          <span className="text-black-200 text-[16px] font-medium">
            industries explored
          </span>
        </div>

        <div className="border-1 border-blue-100 rounded-2xl bg-white py-5 px-4 flex flex-col space-x-2">
          <span className="sm:text-[40px] text-2xl font-bold text-black-200">
            30
          </span>
          <span className="text-black-200 text-[16px] font-medium">
            roles explored
          </span>
        </div>
      </div>
      <h3 className="heading-sm text-[20px] leading-[40px]">
        Difficulty Level Overview
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div className="border-1 border-blue-100 rounded-2xl bg-white py-5 px-4 flex flex-col space-x-2">
          <span className="sm:text-[40px] text-2xl font-bold text-black-200">
            {getDifficultyValue("easy")}
          </span>
          <span className="text-black-200 text-[16px] font-medium">
            Easy Pitches
          </span>
        </div>

        <div className="border-1 border-blue-100 rounded-2xl bg-white py-5 px-4 flex flex-col space-x-2">
          <span className="sm:text-[40px] text-2xl font-bold text-black-200">
            {getDifficultyValue("medium")}
          </span>
          <span className="text-black-200 text-[16px] font-medium">
            Medium Pitches
          </span>
        </div>

        <div className="border-1 border-blue-100 rounded-2xl bg-white py-5 px-4 flex flex-col space-x-2">
          <span className="sm:text-[40px] text-2xl font-bold text-black-200">
            {getDifficultyValue("hard")}
          </span>
          <span className="text-black-200 text-[16px] font-medium">
            Hard Pitches
          </span>
        </div>
      </div>
    </>
  );
};

export default OldDashboard;
